import React, { useMemo } from 'react';
import { models } from 'powerbi-client';
import AnalyticsDialog from '../components/AnalyticsDialog';
import EmbeddedPowerBIReport, { PowerBIReportConfig } from '../components/EmbeddedPowerBIReport';

type ViewMode = 'compact' | 'extended';

type InventoryHistoryData = {
    supplierSku?: string | number;
    supplierId?: string | number;
};

type InventoryHistoryAnalyticsDialogProps = {
    open: boolean;
    onClose: () => void;
    viewMode?: ViewMode;
    onViewModeChange?: (mode: ViewMode) => void;
    compactWidth?: number;
    compactHeight?: number;
    extendedWidth?: number;
    extendedHeight?: number;
    data?: InventoryHistoryData | null;
    reportConfig?: PowerBIReportConfig | null;
};

const InventoryHistoryAnalyticsDialog = ({
    open,
    onClose,
    viewMode,
    onViewModeChange,
    compactWidth = 580,
    compactHeight = 650,
    extendedWidth = 1000,
    extendedHeight = 700,
    data = null,
    reportConfig = null
}: InventoryHistoryAnalyticsDialogProps) => {

    const doClose = () => {
        onClose();
    }

    const chart = useMemo(() => {
        return reportConfig && data && data.supplierSku && data.supplierId && (
            <EmbeddedPowerBIReport
                reportConfig={reportConfig}
                pageName="inv-trend-chart"
                filters={[
                    {
                        $schema: "http://powerbi.com/product/schema#basic",
                        target: { table: "fact_inventory_latest", column: "SupplierSku" },
                        operator: "In",
                        values: [data.supplierSku],
                        filterType: models.FilterType.Basic
                    },
                    {
                        $schema: "http://powerbi.com/product/schema#basic",
                        target: { table: "fact_inventory_latest", column: "SupplierCoId" },
                        operator: "In",
                        values: [data.supplierId],
                        filterType: models.FilterType.Basic
                    },
                ]}
                style={{ width: '100%', height: '100%', background: 'transparent' }}
            />
        );
    }, [reportConfig, data]);

    const table = useMemo(() => {
        return reportConfig && data && data.supplierSku && data.supplierId && (
            <EmbeddedPowerBIReport
                reportConfig={reportConfig}
                pageName="inv-table"
                filters={[
                    {
                        $schema: "http://powerbi.com/product/schema#basic",
                        target: { table: "fact_inventory_latest", column: "SupplierSku" },
                        operator: "In",
                        values: [data.supplierSku],
                        filterType: models.FilterType.Basic
                    },
                    {
                        $schema: "http://powerbi.com/product/schema#basic",
                        target: { table: "fact_inventory_latest", column: "SupplierCoId" },
                        operator: "In",
                        values: [data.supplierId],
                        filterType: models.FilterType.Basic
                    },
                ]}
                style={{ width: '100%', height: '100%', background: 'transparent' }}
            />
        );
    }, [reportConfig, data]);

    const volatility = useMemo(() => {
        return reportConfig && data && (
            <EmbeddedPowerBIReport
                reportConfig={reportConfig}
                pageName="inv-volatility"
                filters={[{
                    $schema: "http://powerbi.com/product/schema#basic",
                    target: { table: "fact_inventory_latest", column: "SupplierSku" },
                    operator: "In",
                    values: [data.supplierSku],
                    filterType: models.FilterType.Basic
                }]}
                style={{ width: '100%', height: '100%', background: 'transparent' }}
            />
        );
    }, [reportConfig, data]);

    return (
        <AnalyticsDialog
            open={open}
            onClose={doClose}
            title="Inventory History"
            // NOTE: AnalyticsDialog does not currently read a controlled `viewMode`
            // prop (only `initialViewMode`); this pass-through mirrors the
            // pre-migration .jsx behavior as-is. It's inert while
            // enableShowMore={false} below keeps AnalyticsDialog's own view-mode
            // toggle from ever rendering.
            viewMode={viewMode}
            onViewModeChange={onViewModeChange}
            compactWidth={compactWidth}
            compactHeight={compactHeight}
            extendedWidth={extendedWidth}
            extendedHeight={extendedHeight}
            enableShowMore={false}
        >
            <div className="analytics-dialog" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
                <div data-testid="compact-panel" style={{ flex: 1, display: viewMode === 'compact' ? 'flex' : 'none' }}>
                    <div style={{ flex: 1, paddingLeft: 5, paddingRight: 5 }}>{table}</div>
                </div>
                <div data-testid="extended-panel" style={{ flex: 1, display: viewMode === 'extended' ? 'flex' : 'none', flexDirection: 'row' }}>
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ flex: .4, paddingBottom: 15 }}>{chart}</div>
                        <div style={{ flex: .6, display: 'flex', flexDirection: 'row' }}>
                            <div style={{ flex: 1, paddingRight: 5 }}>{volatility}</div>
                            <div style={{ flex: 1, paddingLeft: 5 }}>{volatility}</div>
                        </div>
                    </div>
                    <div style={{ flex: .45 }}>{table}</div>
                </div>
            </div>

        </AnalyticsDialog>
    );

}

export default InventoryHistoryAnalyticsDialog;
